import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const { user, refreshUser } = useAuth();
  const isMurid = user?.role === "murid";
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [username, setUsername] = useState("");
  const [savingUname, setSavingUname] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unameStatus, setUnameStatus] = useState(null); // checking | available | current | taken | empty

  useEffect(() => {
    if (open) { setUsername(user?.username || ""); setUnameStatus(null); }
  }, [open, user?.username]);

  useEffect(() => {
    if (!open || !isMurid) return;
    const uname = username.trim().toLowerCase();
    if (!uname) { setUnameStatus("empty"); return; }
    if (uname === user?.username) { setUnameStatus("current"); return; }
    setUnameStatus("checking");
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/auth/username-available", { params: { username: uname } });
        setUnameStatus(r.data.available ? "available" : "taken");
      } catch (e) {
        setUnameStatus(null);
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, open, isMurid, user?.username]);

  const submitUsername = async () => {
    const uname = username.trim().toLowerCase();
    if (!uname) return toast.error("Username tidak boleh kosong");
    if (uname === user?.username) return toast.error("Username masih sama dengan sebelumnya");
    setSavingUname(true);
    try {
      await api.put("/auth/change-username", { username: uname });
      await refreshUser();
      toast.success("Username berhasil diganti");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengganti username");
    } finally {
      setSavingUname(false);
    }
  };

  const submit = async () => {
    if (form.new_password.length < 6) return toast.error("Kata sandi baru minimal 6 karakter");
    if (form.new_password !== form.confirm) return toast.error("Konfirmasi kata sandi tidak cocok");
    setSaving(true);
    try {
      await api.put("/auth/change-password", { old_password: form.old_password, new_password: form.new_password });
      toast.success("Kata sandi berhasil diganti");
      setForm({ old_password: "", new_password: "", confirm: "" });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengganti kata sandi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display">{isMurid ? "Pengaturan Akun" : "Ganti Kata Sandi"}</DialogTitle></DialogHeader>

        {isMurid && (
          <div className="space-y-3 pb-4 mb-1 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-700">Username</p>
            <div>
              <Label>Username untuk Masuk</Label>
              <div className="relative mt-1">
                <Input value={username} onChange={(e) => setUsername(e.target.value)} className="rounded-xl pr-10" data-testid="change-username-input" placeholder="username" />
                <span className="absolute inset-y-0 right-0 flex items-center pr-3" data-testid="username-status-icon">
                  {unameStatus === "checking" && <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />}
                  {(unameStatus === "available" || unameStatus === "current") && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                  {unameStatus === "taken" && <XCircle className="w-5 h-5 text-rose-500" />}
                </span>
              </div>
              {unameStatus === "taken" ? (
                <p className="text-xs text-rose-500 mt-1" data-testid="username-status-text">Username sudah dipakai, coba nama lain ya</p>
              ) : unameStatus === "available" ? (
                <p className="text-xs text-emerald-600 mt-1" data-testid="username-status-text">Username tersedia! 🎉</p>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Guru tetap bisa melihat username barumu di daftar murid.</p>
              )}
            </div>
            <Button onClick={submitUsername} disabled={savingUname || unameStatus === "taken" || unameStatus === "checking"} variant="outline" className="rounded-xl" data-testid="change-username-submit">
              {savingUname && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Username
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {isMurid && <p className="text-sm font-semibold text-slate-700">Ganti Kata Sandi</p>}
          <div><Label>Kata Sandi Lama</Label><PasswordInput value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} className="mt-1 rounded-xl" data-testid="old-password-input" /></div>
          <div><Label>Kata Sandi Baru</Label><PasswordInput value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1 rounded-xl" data-testid="new-password-input" /></div>
          <div><Label>Ulangi Kata Sandi Baru</Label><PasswordInput value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1 rounded-xl" data-testid="confirm-password-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="change-password-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Kata Sandi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
